import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useUnmatchedProducts } from '@/hooks/useInventorySyncReport';
import { useAddProductVariation } from '@/hooks/use-product-variations';
import { useGetProductsWithDetails } from '@/hooks/use-products-with-details';
import { useCreateProduct, useBulkCreateProducts } from '@/hooks/use-product-management';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { AlertTriangle, ArrowLeft, Search, Download, Link2, Plus, PlusCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

interface MappingTarget {
  productName: string;
  source: string;
}

export default function UnmatchedProducts() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading } = useUnmatchedProducts();
  const { data: products } = useGetProductsWithDetails();
  const addVariation = useAddProductVariation();
  const createProduct = useCreateProduct();
  const bulkCreate = useBulkCreateProducts();
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'B2B' | 'ChipChip'>('all');
  const [mappingTarget, setMappingTarget] = useState<MappingTarget | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [addTarget, setAddTarget] = useState<string | null>(null);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.filter((item) => {
      const matchesSearch = item.productName.toLowerCase().includes(search.toLowerCase());
      const matchesSource = sourceFilter === 'all' || item.source === sourceFilter;
      return matchesSearch && matchesSource;
    });
  }, [data, search, sourceFilter]);

  const b2bCount = data?.filter((d) => d.source === 'B2B').length || 0;
  const chipchipCount = data?.filter((d) => d.source === 'ChipChip').length || 0;

  const exportCsv = () => {
    if (!filtered.length) return;
    const header = 'Source,Product Name,Last Seen,Occurrences\n';
    const rows = filtered
      .map((r) => `${r.source},"${r.productName}",${r.lastSeen},${r.occurrences}`)
      .join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'unmatched-products.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleMap = (productName: string, source: string) => {
    setMappingTarget({ productName, source });
    setSelectedProductId(null);
  };

  const handleConfirmMapping = () => {
    if (!mappingTarget || !selectedProductId) return;
    addVariation.mutate(
      { productId: selectedProductId, variationName: mappingTarget.productName },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['unmatched-products'] });
          setMappingTarget(null);
          setSelectedProductId(null);
        },
      }
    );
  };

  const handleAddAsProduct = () => {
    if (!addTarget) return;
    createProduct.mutate(addTarget, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['unmatched-products'] });
        toast.success(`"${addTarget}" added as a new product.`);
        setAddTarget(null);
      },
      onError: (err) => {
        toast.error(`Failed to add product: ${err.message}`);
      },
    });
  };

  const handleBulkAdd = () => {
    const names = filtered.map((item) => item.productName);
    if (!names.length) return;
    bulkCreate.mutate(names, {
      onSuccess: (result) => {
        queryClient.invalidateQueries({ queryKey: ['unmatched-products'] });
        toast.success(`Added ${result.added} product(s). ${result.skipped} skipped (duplicates).`);
        setShowBulkConfirm(false);
      },
      onError: (err) => {
        toast.error(`Bulk add failed: ${err.message}`);
      },
    });
  };

  const selectedProductName = products?.find((p) => p.id === selectedProductId)?.name;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/inventory/sync-report')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Unmatched Products</h1>
            <p className="text-muted-foreground">
              Product names from sync data that don't match any internal product or variation.
            </p>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{data?.length || 0}</div>
              <p className="text-sm text-muted-foreground">Total Unmatched</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{b2bCount}</div>
              <p className="text-sm text-muted-foreground">From B2B</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{chipchipCount}</div>
              <p className="text-sm text-muted-foreground">From ChipChip</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Unmatched Product List
            </CardTitle>
            <CardDescription>
              These need to be added to the product list or mapped as variations.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search product name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex gap-2">
                {(['all', 'B2B', 'ChipChip'] as const).map((s) => (
                  <Button
                    key={s}
                    variant={sourceFilter === s ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSourceFilter(s)}
                  >
                    {s === 'all' ? 'All' : s}
                  </Button>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBulkConfirm(true)}
                disabled={!filtered.length || bulkCreate.isPending}
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                Add All ({filtered.length})
              </Button>
              <Button variant="outline" size="sm" onClick={exportCsv} disabled={!filtered.length}>
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>

            {isLoading ? (
              <Skeleton className="h-64" />
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Source</TableHead>
                      <TableHead>Product Name</TableHead>
                      <TableHead>Last Seen</TableHead>
                      <TableHead className="text-right">Occurrences (30d)</TableHead>
                      <TableHead className="w-[140px]">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-12">
                          {search || sourceFilter !== 'all'
                            ? 'No matching products found.'
                            : 'All products matched! No unmatched products found.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filtered.map((item) => (
                        <TableRow key={`${item.source}-${item.productName}`}>
                          <TableCell>
                            <Badge variant={item.source === 'B2B' ? 'default' : 'secondary'}>
                              {item.source}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium">{item.productName}</TableCell>
                          <TableCell>{item.lastSeen}</TableCell>
                          <TableCell className="text-right font-mono">{item.occurrences}</TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setAddTarget(item.productName)}
                              >
                                <Plus className="h-3.5 w-3.5 mr-1" />
                                Add
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleMap(item.productName, item.source)}
                              >
                                <Link2 className="h-3.5 w-3.5 mr-1" />
                                Map
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              Showing {filtered.length} of {data?.length || 0} unmatched products from the last 30 days.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Add as Product Confirmation Dialog */}
      <Dialog open={!!addTarget} onOpenChange={(open) => !open && setAddTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add as New Product</DialogTitle>
            <DialogDescription>
              This will create a new product named <span className="font-semibold text-foreground">"{addTarget}"</span> in your product list.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddTarget(null)}>
              Cancel
            </Button>
            <Button onClick={handleAddAsProduct} disabled={createProduct.isPending}>
              {createProduct.isPending ? 'Adding...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Add Confirmation Dialog */}
      <Dialog open={showBulkConfirm} onOpenChange={setShowBulkConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add All as New Products</DialogTitle>
            <DialogDescription>
              This will create <span className="font-semibold text-foreground">{filtered.length}</span> new product(s) from the current filtered list. Duplicates will be skipped automatically.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkConfirm(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkAdd} disabled={bulkCreate.isPending}>
              {bulkCreate.isPending ? 'Adding...' : `Add ${filtered.length} Products`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Map to Product Dialog */}
      <Dialog open={!!mappingTarget} onOpenChange={(open) => !open && setMappingTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Map to Internal Product</DialogTitle>
            <DialogDescription>
              Map <span className="font-semibold text-foreground">"{mappingTarget?.productName}"</span> as a variation of an existing product.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-md border">
            <Command>
              <CommandInput placeholder="Search products..." />
              <CommandList>
                <CommandEmpty>No products found.</CommandEmpty>
                <CommandGroup>
                  {(products || []).map((product) => (
                    <CommandItem
                      key={product.id}
                      value={product.name}
                      onSelect={() => setSelectedProductId(product.id)}
                      className={selectedProductId === product.id ? 'bg-accent' : ''}
                    >
                      {product.name}
                      {selectedProductId === product.id && (
                        <span className="ml-auto text-xs text-muted-foreground">Selected</span>
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </div>

          {selectedProductName && (
            <p className="text-sm text-muted-foreground">
              "{mappingTarget?.productName}" will be saved as a variation of <span className="font-semibold text-foreground">{selectedProductName}</span>.
            </p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setMappingTarget(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmMapping}
              disabled={!selectedProductId || addVariation.isPending}
            >
              {addVariation.isPending ? 'Saving...' : 'Confirm Mapping'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}